import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { useNavigation } from '@react-navigation/native'
import type { StackNavigationProp } from '@react-navigation/stack'
import type { AppStackParamList, CommonAuthorizedStackParamList } from '@src/navigation/types'
import {
  onboardingSkippedSelector,
  uiStatesSelector,
  uxSensorModeKindSelector,
} from '@src/selectors/app'
import {
  Maybe,
  SurveyLink,
  SurveysConfigKind,
  SurveysQuestionKey,
  UiStateNames,
  UserExperienceSensorModeKind,
} from '@src/types'
import { TutorialSensorKind } from '@src/screens/Tutorials/models/tutorials.types'
import { useHasOwnSensorSubscription } from '@src/hooks/useHasOwnSensorSubscription'
import { Storage, useDispatchAsync, useSnack } from '@src/utils'
import { Bugsnag } from '@src/config'
import { useIsHealthPermissionRequired } from './utils'

export const useShouldShowByosOnboardingTutorials = () => {
  const onboardingSkipped = useSelector(onboardingSkippedSelector)
  const shouldShowByosOnboarding = useShouldShowByosOnboarding()
  const uiStates = useSelector(uiStatesSelector)

  if (!shouldShowByosOnboarding || onboardingSkipped) {
    return false
  }

  const tutorialCompletedUiState = uiStates.find(
    (uiState) => uiState.name === UiStateNames.OwnSensorTutorialCompleted,
  )
  const tutorialCompleted = tutorialCompletedUiState?.value || false

  return !tutorialCompleted
}

export const useShouldShowByosOnboarding = () => {
  const onboardingSkipped = useSelector(onboardingSkippedSelector)
  const uiStates = useSelector(uiStatesSelector)
  const hasOwnSensorSubscription = useHasOwnSensorSubscription()

  if (onboardingSkipped || !hasOwnSensorSubscription) {
    return false
  }

  const onboardingCompletedUiState = uiStates.find(
    (uiState) => uiState.name === UiStateNames.OwnSensorOnboardingCompleted,
  )
  const onboardingCompleted = onboardingCompletedUiState?.value || false

  return !onboardingCompleted
}

export const useByosOnboardingTutorial = (): TutorialSensorKind | null => {
  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)
  const sensorModeTutorialsMap = useGetSensorModeTutorials()

  return uxSensorModeKind ? sensorModeTutorialsMap[uxSensorModeKind] || null : null
}

// Fallback for BYOS onboarding screens when there is no tutorial mapping
// (e.g. uxSensorModeKind is missing or unmapped).
export const useExitByosOnboarding = () => {
  const dispatch = useDispatch()
  const authorizedNavigation = useNavigation<StackNavigationProp<CommonAuthorizedStackParamList>>()
  const appNavigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const isOnboardingInProgress = useShouldShowByosOnboarding()

  return useCallback(() => {
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.OwnSensorOnboardingCompleted,
        value: true,
      },
    })

    if (isOnboardingInProgress) {
      authorizedNavigation.replace('AppExplorationPrompt')
      return
    }

    appNavigation.replace('Drawer')
  }, [appNavigation, authorizedNavigation, dispatch, isOnboardingInProgress])
}

const useGetSensorModeTutorials = (): Partial<
  Record<UserExperienceSensorModeKind, TutorialSensorKind>
> => {
  return useMemo(() => {
    return {
      [UserExperienceSensorModeKind.OwnSensorDexcomStelo]: TutorialSensorKind.DexcomStelo,
      [UserExperienceSensorModeKind.DexcomStelo]: TutorialSensorKind.DexcomSteloRealtime,
      [UserExperienceSensorModeKind.Lingo]: TutorialSensorKind.LingoThirdParty,
      [UserExperienceSensorModeKind.OwnSensorDexcomG6]: TutorialSensorKind.DexcomG6Realtime,
      [UserExperienceSensorModeKind.OwnSensorDexcomG7]: TutorialSensorKind.DexcomG7Realtime,
      [UserExperienceSensorModeKind.OwnSensorLibre1]: TutorialSensorKind.Libre123IndirectSyncing,
      [UserExperienceSensorModeKind.OwnSensorLibre2]: TutorialSensorKind.Libre123IndirectSyncing,
      [UserExperienceSensorModeKind.OwnSensorLibre3]: TutorialSensorKind.Libre123IndirectSyncing,
    }
  }, [])
}

export const useIsIndirectSyncSensorMode = () => {
  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)

  if (!uxSensorModeKind) {
    return false
  }

  return [
    UserExperienceSensorModeKind.OwnSensorLibre1,
    UserExperienceSensorModeKind.OwnSensorLibre2,
    UserExperienceSensorModeKind.OwnSensorLibre3,
  ].includes(uxSensorModeKind)
}

const SENSOR_APP_NAMES: Partial<Record<UserExperienceSensorModeKind, string>> = {
  [UserExperienceSensorModeKind.OwnSensorDexcomG6]: 'Dexcom G6',
  [UserExperienceSensorModeKind.OwnSensorDexcomG7]: 'Dexcom G7',
  [UserExperienceSensorModeKind.OwnSensorDexcomStelo]: 'Stelo by Dexcom',
  [UserExperienceSensorModeKind.DexcomStelo]: 'Stelo by Dexcom',
  [UserExperienceSensorModeKind.Lingo]: 'Lingo',
  [UserExperienceSensorModeKind.OwnSensorLibre1]: 'FreeStyle Libre 14-Day System (Libre 1)',
  [UserExperienceSensorModeKind.OwnSensorLibre2]: 'FreeStyle Libre 2',
  [UserExperienceSensorModeKind.OwnSensorLibre3]: 'FreeStyle Libre 3',
}

export const useGetThirdPartyAppName = () => {
  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)

  if (!uxSensorModeKind || !SENSOR_APP_NAMES[uxSensorModeKind]) {
    return 'Unknown Sensor'
  }

  return SENSOR_APP_NAMES[uxSensorModeKind]
}

export const useSyncsGlucoseViaAppleOrGoogle = () => {
  const isHealthPermissionRequired = useIsHealthPermissionRequired()

  return isHealthPermissionRequired
}

export const useSubmitOwnSensorSurvey = () => {
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()

  const submitOwnSensorSurvey = useCallback(async () => {
    const selectedSensorType = Storage.get(Storage.ONBOARDING_SENSOR_TYPE_SELECTED_KEY)
    if (!selectedSensorType) {
      return
    }

    try {
      let surveyLink = await dispatchAsync<Maybe<SurveyLink>>({
        type: 'app/latestSurveyLinkByKind',
        payload: {
          kind: SurveysConfigKind.OwnSensor,
        },
      })

      // don't start survey if last one is in progress
      if (surveyLink === null || surveyLink.finished) {
        surveyLink = await dispatchAsync({
          type: 'app/startQuestionnaire',
          payload: { kind: SurveysConfigKind.OwnSensor },
        })
      }

      if (!surveyLink) {
        const error = `Survey link is undefined in useSubmitOwnSensorSurvey`
        throw new Error(error)
      }

      // Save the survey response
      await dispatchAsync({
        type: 'app/saveSurveyResponse',
        payload: {
          surveyLinkId: surveyLink.id,
          questionKey: SurveysQuestionKey.CgmDevice,
          answer: { value: selectedSensorType },
          warnBeforeStopping: true,
        },
      })

      // Complete the survey
      await dispatchAsync({
        type: 'app/completeSurvey',
        payload: { id: surveyLink.id },
      })
    } catch (error: any) {
      let message = error?.message || 'OwnSensor survey - unknown error occurred'
      if (error?.message?.includes('breaking_outcome')) {
        message = 'OwnSensor survey - breaking outcome'
        showSnack('This sensor is not supported. Please contact support.')
      } else if (error?.message?.includes('Surveys config not found')) {
        message = 'OwnSensor survey - config not found'
        showSnack('Survey not found. Please contact support.')
      } else {
        showSnack('Failed to submit sensor selection. Please try again.')
      }

      Bugsnag.notify(message)

      throw error
    }
  }, [dispatchAsync, showSnack])

  return submitOwnSensorSurvey
}
